// index.js
import { useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    gridDatasetSelector,
    createFetchGridDataset
} from '@libs/datasets';
import { makeSideEffectCallback } from '@libs/makeContext';

import {
    YearlyTimesheetsProvider,
    useYearlyTimesheetsState
} from './useYearlyTimesheets';
import { YEARLY_ALL_ATTENDANCE } from 'timeAndAttendance/redux.datasets';
import { DOMAIN_NAME } from 'timeAndAttendance/common';

import { StatusLegend } from 'timeAndAttendance/components';
import { setTimesheetExportContext } from '../exportContext';
import Filters from './Filters';
import UsersTimesheet from './UsersTimesheet';
import Pagination from './Pagination';

/** Publishes the view's current filters for the export modal to pre-fill. */
const ExportContextSync = () => {
    const { year, email } = useYearlyTimesheetsState();
    useEffect(() => {
        setTimesheetExportContext('yearly', { year, email });
    }, [year, email]);
    return null;
};

const TimesheetsYearlyView = () => {
    // 1️⃣ Select yearly dataset from Redux
    const { data, currentPage, totalPages } = useSelector((state) =>
        gridDatasetSelector(state, DOMAIN_NAME, YEARLY_ALL_ATTENDANCE)
    );

    const dispatch = useDispatch();

    // 3️⃣ Grid dataset fetcher (used by middleware for pagination/filtering)
    const fetchGridYearlyAllDataset = useCallback(
        (payload) =>
            dispatch(
                createFetchGridDataset(
                    DOMAIN_NAME,
                    YEARLY_ALL_ATTENDANCE
                )(payload)
            ),
        [dispatch]
    );

    // 4️⃣ Initialize provider state with Redux dataset
    const initialize = useCallback(
        (state) => ({
            ...state,
            data,
            totalPages,
            currentPage
        }),
        [data, totalPages, currentPage]
    );

    // 5️⃣ Provide fetch callback to middleware (used inside useYearlyTimesheets)
    const middlewareProps = useMemo(
        () => ({
            fetchData: makeSideEffectCallback(fetchGridYearlyAllDataset)
        }),
        [fetchGridYearlyAllDataset]
    );

    // 7️⃣ Render the full page wrapped in provider
    return (
        <YearlyTimesheetsProvider
            initialize={initialize}
            middlewareProps={middlewareProps}
        >
            <ExportContextSync />
            <Filters />
            <StatusLegend sx={{ px: 2, pb: 1.5 }} />
            <UsersTimesheet />
            <Pagination />
        </YearlyTimesheetsProvider>
    );
};

export default TimesheetsYearlyView;
